<script>
import { required } from "vuelidate/lib/validators";
import DatePicker from "vue2-datepicker";

export default {
    name: "DVReleaseModal",
    components: {
        DatePicker
    },
    data() {
        return {
            form: {
                isRelease: true,
                dateReleased: new Date(),
                receivedBy: "",
                payeeORNumber: "",
                payeeORDate: new Date()
            },
            submitted: false,
            disbursementData: null
        };
    },
    validations: {
        form: {
            receivedBy: { required },
            payeeORNumber: { required },
            dateReleased: { required },
            payeeORDate: { required }
        }
    },
    methods: {
        resetForm() {
            this.form = {
                isRelease: true,
                dateReleased: new Date(),
                receivedBy: "",
                payeeORNumber: "",
                payeeORDate: new Date()
            };
            this.submitted = false;
            this.$v.form.$reset();
        },
        setDisbursementData(data) {
            this.disbursementData = data;
        },
        onSubmit() {
            this.submitted = true;
            this.$v.form.$touch();
            
            if (this.$v.form.$invalid) {
                return;
            }
            const releaseData = {
                id: this.disbursementData?.id,
                isRelease: this.form.isRelease,
                dateReleased: this.getDate(this.form.dateReleased),
                receivedBy: this.form.receivedBy,
                payeeORNumber: this.form.payeeORNumber,
                payeeORDate: this.getDate(this.form.payeeORDate)
            };

            this.$emit("submit", releaseData);
            this.$bvModal.hide("dv-release-modal");
        },
    }
};
</script>

<template>
    <b-modal
        id="dv-release-modal"
        title="Release Disbursement Voucher"
        size="md"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <form @submit.prevent="onSubmit" class="needs-validation">
            <!-- Disbursement Information Display -->
            <div class="border p-3 mb-3 rounded">
                <h6 class="text-muted mb-2">Disbursement Information</h6>
                <div v-if="disbursementData">
                    <div class="row">
                        <div class="col-6">
                            <small class="text-muted">DV Number:</small>
                            <p class="mb-1 fw-bold">{{ disbursementData.disbursementNumber || 'N/A' }}</p>
                        </div>
                        <div class="col-6">
                            <small class="text-muted">Amount:</small>
                            <p class="mb-1 fw-bold text-success">{{ formatCurrency(disbursementData.amount || 0) }}</p>
                        </div>
                        <div class="col-12">
                            <small class="text-muted">Particulars:</small>
                            <p class="mb-0 small">{{ disbursementData.particulars || 'N/A' }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Release Form -->
            <div class="mb-3">
                <div class="form-check form-checkbox-outline form-check-success">
                    <input
                        type="checkbox"
                        id="isRelease"
                        class="form-check-input"
                        v-model="form.isRelease"
                    />
                    <label for="isRelease" class="form-check-label">
                        Mark as Released
                    </label>
                </div>
            </div>

            <div class="mb-3" v-if="form.isRelease">
                <label for="dateReleased" class="form-label">Date Released <span class="text-danger">*</span></label>
                <date-picker
                    id="dateReleased"
                    v-model="form.dateReleased"
                    :first-day-of-week="1"
                    lang="en"
                    placeholder="Select Release Date..."
                    format="MMMM DD, YYYY"
                    :class="{
                        'is-invalid': submitted && $v.form.dateReleased.$error
                    }"
                />
                <div
                    v-if="submitted && $v.form.dateReleased.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.dateReleased.required">Date released is required.</span>
                </div>
            </div>

            <div class="mb-3" >
                <label for="receivedBy" class="form-label">Received By <span class="text-danger">*</span></label>
                <b-form-input
                    id="receivedBy"
                    v-model="form.receivedBy"
                    placeholder="Enter name of person who received..."
                    autocomplete="off"
                    :class="{
                        'is-invalid': submitted && $v.form.receivedBy.$error
                    }"
                />
                <div
                    v-if="submitted && $v.form.receivedBy.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.receivedBy.required">Received by field is required.</span>
                </div>
            </div>

            <div class="mb-3">
                <label for="payeeORNumber" class="form-label">Payee OR Number <span class="text-danger">*</span></label>
                <b-form-input
                    id="payeeORNumber"
                    v-model="form.payeeORNumber"
                    placeholder="Enter OR Number..."
                    autocomplete="off"
                    :class="{
                        'is-invalid': submitted && $v.form.payeeORNumber.$error
                    }"
                />
                <div
                    v-if="submitted && $v.form.payeeORNumber.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.payeeORNumber.required">OR Number is required.</span>
                </div>
            </div>

            <div class="mb-3">
                <label for="payeeORDate" class="form-label">Payee OR Date <span class="text-danger">*</span></label>
                <date-picker
                    id="payeeORDate"
                    v-model="form.payeeORDate"
                    :first-day-of-week="1"
                    lang="en"
                    placeholder="Select OR Date..."
                    format="MMMM DD, YYYY"
                    :class="{
                        'is-invalid': submitted && $v.form.payeeORDate.$error
                    }"
                />
                <div
                    v-if="submitted && $v.form.payeeORDate.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.payeeORDate.required">OR Date is required.</span>
                </div>
            </div>

            <!-- Modal Footer -->
            <div class="d-flex justify-content-end gap-2 mt-4">
                <button 
                    type="button" 
                    class="btn btn-secondary" 
                    @click="$bvModal.hide('dv-release-modal')"
                >
                    <i class="bx bx-x me-1"></i>Cancel
                </button>
                <button 
                    type="submit" 
                    class="btn btn-success"
                >
                    <i class="bx bx-check me-1"></i>Release DV
                </button>
            </div>
        </form>
    </b-modal>
</template>

<style scoped>
.bg-light {
    background-color: #f8f9fa !important;
}
</style>